import CheckIcon from '@heroicons/react/24/outline/CheckIcon';
import XCircleIcon from '@heroicons/react/24/outline/XCircleIcon';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { putProjectTemplateTDMImport } from '@/api/Sherpa/ProjectTemplate';
import type { IPutProjectTemplateTDMImportPayload } from '@/api/Sherpa/ProjectTemplate/types';
import AccentTitle from '@/components/UI/AccentTitle';
import { Accordion, AccordionItem } from '@/components/UI/Accordion';
import Button from '@/components/UI/Button';
import { HookInput } from '@/components/UI/HookForm/Form/Input';
import { HookReactSingleSelect } from '@/components/UI/HookForm/Form/ReactSelect';
import { SherpaProjectReactQueryKey } from '@/constants/ReactQueryKeys';
import { useCategories, useCategorySubGroup } from '@/hooks/ReactQueryHooks/useCategoryQuery';
import { useOrgTitles, useTitleByName } from '@/hooks/ReactQueryHooks/useTitlesQuery';
import { useCSVParser } from '@/store/Context/CSVParserContext';
import { notifyUser } from '@/store/Toast';
import type { Category } from '@/types/Category';
import { normalize } from '@/utils/normalize';
import { type EditActionFn, Table, type TableHeader } from './Table';
import type { TDM } from './types';

type Phases = 'Acquisition' | 'Finance' | 'Procurement' | 'Development' | 'Design' | 'Construction';
type PhaseWiseData = {
  [K in Phases]: Record<string, any>[];
};

type PhaseWiseError = {
  [K in Phases]: {
    categories: {
      [id: string]: string; // table array index with error message
    };
    lead: {
      [id: string]: string;
    };
    support: {
      [id: string]: string;
    };
  };
};
type TdmError = {
  [K in TDM]: PhaseWiseError;
};

const correctPhaseNames = ['Acquisition', 'Finance', 'Procurement', 'Development', 'Design', 'Construction'];

const defaultPhaseWiseData = {
  Acquisition: [],
  Finance: [],
  Procurement: [],
  Development: [],
  Design: [],
  Construction: [],
};

const defaultPhaseWiseErrors = {
  Acquisition: {
    categories: {},
    lead: {},
    support: {},
  },
  Finance: {
    categories: {},
    lead: {},
    support: {},
  },
  Procurement: {
    categories: {},
    lead: {},
    support: {},
  },
  Development: {
    categories: {},
    lead: {},
    support: {},
  },
  Design: {
    categories: {},
    lead: {},
    support: {},
  },
  Construction: {
    categories: {},
    lead: {},
    support: {},
  },
};
const defaultValidationErrors = {
  task: defaultPhaseWiseErrors,
  deliverable: defaultPhaseWiseErrors,
  milestone: defaultPhaseWiseErrors,
};

export const ReviewContents = () => {
  const { templateId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [availablePhases, setAvailablePhases] = useState<Phases[]>([]);
  const [validationErrorsState, setValidationErrorsState] = useState<TdmError>(defaultValidationErrors);
  const [currentPhase, setCurrentPhase] = useState<number>(0);
  const [normalizedCategoriesByName, setNormalizedCategoriesByName] = useState<{
    [id: string]: Category;
  }>({});
  const { setTransformedData, data, transformedData } = useCSVParser();
  const [phaseWiseTaskData, setPhaseWiseTaskData] = useState<PhaseWiseData>(defaultPhaseWiseData);
  const [phaseWiseDeliverableData, setPhaseWiseDeliverableData] = useState<PhaseWiseData>(defaultPhaseWiseData);
  const [phaseWiseMilestoneData, setPhaseWiseMilestoneData] = useState<PhaseWiseData>(defaultPhaseWiseData);

  const fetchCategoriesSubGroup = useCategorySubGroup({});
  const categoriesSubGroup = fetchCategoriesSubGroup?.data?.data;

  const phaseCategory = categoriesSubGroup?.find((a: any) => a.name === availablePhases[currentPhase]?.toLowerCase());

  const categoriesQuery = useCategories({
    params: { subGroupId: phaseCategory?.id },
    onSuccess: ({ data }) => {
      const normalizedData = normalize<Category>(data, 'name');
      setNormalizedCategoriesByName(normalizedData || {});
    },
  });

  const categoriesData = categoriesQuery.data?.data || [];

  const fetchTitles = useOrgTitles({});
  const titles = fetchTitles?.data?.data;

  const { getTitle } = useTitleByName();

  const generatePhaseWiseCategoryError = (tdmType: TDM, phase: Phases, category: string) => {
    const categoryData = normalizedCategoriesByName[(category || '').trim()];

    if (category) {
      if (!categoryData) {
        return "Category does't exist";
      }
      if (categoryData.parentGroup?.name.toLowerCase() !== tdmType.toLowerCase()) {
        return `Not a valid category for ${tdmType.charAt(0).toUpperCase()}${tdmType.slice(1)}`;
      }
      if (categoryData.subGroup?.name !== phase.toLowerCase()) {
        return `Not a valid category for ${phase} phase`;
      }
    }
    return undefined;
  };

  const generateTitleError = (title: string) => {
    if (title.length > 0) {
      const matchedTitle = titles?.find((t) => t.name === title.trim());

      if (!matchedTitle) {
        return `${title} title does not exist in your account.`;
      }
    }
    return undefined;
  };

  const categorizeData = (data: Record<string, any>[], tdmType: TDM) => {
    return data.reduce<{
      phases: Phases[];
      validationErrors: PhaseWiseError;
      categorizedData: PhaseWiseData;
    }>(
      (acc, current) => {
        const { phase, category, lead, support } = current;

        const categoryData = normalizedCategoriesByName[(category || '').trim()];

        const phaseWiseTdmTypeErrors = acc.validationErrors[phase as keyof typeof acc.validationErrors] || {
          categories: {},
          lead: {},
          support: {},
        };
        if (phase) {
          acc = {
            ...acc,
            phases: [...acc.phases, phase],
            validationErrors: {
              ...acc.validationErrors,

              [phase]: {
                ...phaseWiseTdmTypeErrors,
                categories: {
                  ...phaseWiseTdmTypeErrors.categories,
                  [current[tdmType]]: generatePhaseWiseCategoryError(tdmType, phase, category),
                },
                lead: {
                  ...phaseWiseTdmTypeErrors.lead,
                  [current[tdmType]]: generateTitleError(lead),
                },
                support: {
                  ...phaseWiseTdmTypeErrors.support,
                  [current[tdmType]]: generateTitleError(support),
                },
              },
            },
            categorizedData: {
              ...acc.categorizedData,
              [phase]: [
                ...(acc.categorizedData[phase as keyof PhaseWiseData] || []),
                {
                  ...current,
                  category: {
                    label: categoryData?.name || category,
                    value: categoryData?.id || category,
                  },
                  lead: {
                    label: getTitle(lead) ? getTitle(lead)?.name : lead,
                    value: getTitle(lead)?.id,
                  },
                  support: {
                    label: getTitle(support) ? getTitle(support)?.name : support,
                    value: getTitle(support)?.id,
                  },
                },
              ],
            },
          };
        }
        return acc;
      },
      {
        phases: [],
        categorizedData: defaultPhaseWiseData,
        validationErrors: defaultPhaseWiseErrors,
      }
    );
  };
  useEffect(() => {
    if (data) {
      const { task, deliverable, milestone, phases, validationErrors } = Object.keys(data).reduce<{
        task: PhaseWiseData;
        deliverable: PhaseWiseData;
        milestone: PhaseWiseData;
        phases: Phases[];
        validationErrors: TdmError;
      }>(
        (acc, currentKey) => {
          const { phases, categorizedData, validationErrors } = categorizeData(data[currentKey as keyof typeof data], currentKey as TDM);
          acc = {
            ...acc,
            phases: [...acc.phases, ...phases],
            validationErrors: {
              ...acc.validationErrors,
              [currentKey]: {
                ...acc.validationErrors[currentKey as keyof typeof acc.validationErrors],
                ...validationErrors,
              },
            },
            [currentKey]: {
              ...acc[currentKey as keyof typeof acc],
              ...categorizedData,
            },
          };
          return acc;
        },
        {
          task: defaultPhaseWiseData,
          deliverable: defaultPhaseWiseData,
          milestone: defaultPhaseWiseData,
          phases: [],
          validationErrors: defaultValidationErrors,
        }
      );
      setPhaseWiseTaskData(task);
      setPhaseWiseDeliverableData(deliverable);
      setPhaseWiseMilestoneData(milestone);
      const set = new Set(phases.filter((phase) => correctPhaseNames.includes(phase)));
      setAvailablePhases(Array.from(set));
      setValidationErrorsState(validationErrors);
    }
  }, [data, categorizeData]);

  const currentPhaseName = availablePhases[currentPhase];

  const taskErrors = validationErrorsState.task;
  const milestoneErrors = validationErrorsState.milestone;
  const deliverableErrors = validationErrorsState.deliverable;

  const currentPhaseTaskErrors = taskErrors[currentPhaseName];
  const currentPhaseMilestoneErrors = milestoneErrors[currentPhaseName];
  const currentPhaseDeliverableErrors = deliverableErrors[currentPhaseName];

  const taskErrorsKeys = Object.keys(currentPhaseTaskErrors || {}).reduce((pv: any[], cv: string) => {
    const v = Object.values(currentPhaseTaskErrors[cv as keyof typeof currentPhaseTaskErrors] || {}).filter(Boolean);

    return [...pv, ...v];
  }, []);

  const deliverablesErrorsKeys = Object.keys(currentPhaseDeliverableErrors || {}).reduce((pv: any[], cv: string) => {
    const v = Object.values(currentPhaseDeliverableErrors[cv as keyof typeof currentPhaseDeliverableErrors] || {}).filter(Boolean);

    return [...pv, ...v];
  }, []);

  const milestonesErrorsKeys = Object.keys(currentPhaseMilestoneErrors || {}).reduce((pv: any[], cv: string) => {
    const v = Object.values(currentPhaseMilestoneErrors[cv as keyof typeof currentPhaseMilestoneErrors] || {}).filter(Boolean);

    return [...pv, ...v];
  }, []);

  const getPhaseErrors = (phase: Phases) => {
    const phaseTaskErrors = taskErrors[phase];
    const phaseMilestoneErrors = milestoneErrors[phase];
    const phaseDeliverableErrors = deliverableErrors[phase];

    const phaseTaskErrorsKeys = Object.keys(phaseTaskErrors || {}).reduce((pv: any[], cv: string) => {
      const v = Object.values(phaseTaskErrors[cv as keyof typeof phaseTaskErrors] || {}).filter(Boolean);

      return [...pv, ...v];
    }, []);

    const phaseDeliverablesErrorsKeys = Object.keys(phaseDeliverableErrors || {}).reduce((pv: any[], cv: string) => {
      const v = Object.values(phaseDeliverableErrors[cv as keyof typeof phaseDeliverableErrors] || {}).filter(Boolean);

      return [...pv, ...v];
    }, []);

    const phaseMilestonesErrorsKeys = Object.keys(phaseMilestoneErrors || {}).reduce((pv: any[], cv: string) => {
      const v = Object.values(phaseMilestoneErrors[cv as keyof typeof phaseMilestoneErrors] || {}).filter(Boolean);

      return [...pv, ...v];
    }, []);

    return {
      tasks: phaseTaskErrorsKeys,
      deliverables: phaseDeliverablesErrorsKeys,
      milestones: phaseMilestonesErrorsKeys,
    };
  };

  const _hasErrors = taskErrorsKeys.length > 0 || deliverablesErrorsKeys.length > 0 || milestonesErrorsKeys.length > 0; // TODO add others as well

  const currentPhaseTaskData = phaseWiseTaskData[currentPhaseName] || [];
  const currentPhaseDeliverableData = phaseWiseDeliverableData[currentPhaseName] || [];
  const currentPhaseMilestoneData = phaseWiseMilestoneData[currentPhaseName] || [];

  const commonHeaders: TableHeader[] = [
    {
      dataAttribute: 'lead',
      name: 'Lead',
      formatter: ({ col: current, isEdit }) => {
        if (isEdit) {
          return (
            <HookReactSingleSelect
              isClearable
              name='lead'
              options={titles?.map((title) => ({
                label: title.name,
                value: title.id,
              }))}
            />
          );
        }
        return current?.label;
      },
    },
    {
      dataAttribute: 'support',
      name: 'Support',
      formatter: ({ col: current, isEdit }) => {
        if (isEdit) {
          return (
            <HookReactSingleSelect
              isClearable
              name='support'
              options={titles?.map((title) => ({
                label: title.name,
                value: title.id,
              }))}
            />
          );
        }
        return current?.label;
      },
    },
  ];

  const taskTableHeader: TableHeader[] = [
    {
      dataAttribute: 'task',
      name: 'Task Name',
      formatter: ({ col, isEdit }) => {
        if (isEdit) {
          return <HookInput type='text' name='task' required />;
        }
        return col;
      },
    },
    {
      dataAttribute: 'category',
      name: 'Category',
      formatter: ({ col: current, isEdit }) => {
        if (isEdit) {
          return (
            <HookReactSingleSelect
              isClearable
              name='category'
              options={categoriesData.map((category: Category) => ({
                label: category.name,
                value: category.id,
              }))}
            />
          );
        }
        return current?.label;
      },
    },
    ...commonHeaders,
  ];

  const milestoneTableHeader: TableHeader[] = [
    {
      dataAttribute: 'milestone',
      name: 'Milestone Name',
      formatter: ({ col, isEdit }) => {
        if (isEdit) {
          return <HookInput type='text' name='milestone' required />;
        }
        return col;
      },
    },
    ...commonHeaders,
  ];

  const deliverableTableHeader: TableHeader[] = [
    {
      dataAttribute: 'deliverable',
      name: 'Deliverable Name',
      formatter: ({ col, isEdit }) => {
        if (isEdit) {
          return <HookInput type='text' name='deliverable' required />;
        }
        return col;
      },
    },
    ...commonHeaders,
  ];
  const isLastAvailablePhase = currentPhase === availablePhases.length;

  const editData = (index: number, collection: PhaseWiseData, updatedRow: any) => {
    const copyData = { ...collection };
    copyData[currentPhaseName].splice(index, 1, updatedRow);
    return copyData;
  };

  const deleteData = (index: number, collection: PhaseWiseData) => {
    const copyData = { ...collection };
    const updatedData = {
      ...copyData,
      [currentPhaseName]: copyData[currentPhaseName].filter((_data, currentIndex) => index !== currentIndex),
    };
    return updatedData;
  };

  const handleTaskEdit: EditActionFn = (row, index) => {
    const updatedData = editData(index, phaseWiseTaskData, row);
    setPhaseWiseTaskData(updatedData);
  };

  const handleTaskDelete: EditActionFn = (_row, index) => {
    const updatedData = deleteData(index, phaseWiseTaskData);
    setPhaseWiseTaskData(updatedData);
  };

  const handleMilestoneEdit: EditActionFn = (row, index) => {
    const updatedData = editData(index, phaseWiseMilestoneData, row);
    setPhaseWiseMilestoneData(updatedData);
  };

  const handleMilestoneDelete: EditActionFn = (_row, index) => {
    const updatedData = deleteData(index, phaseWiseMilestoneData);
    setPhaseWiseMilestoneData(updatedData);
  };

  const handleDeliverableEdit: EditActionFn = (row, index) => {
    const updatedData = editData(index, phaseWiseDeliverableData, row);
    setPhaseWiseDeliverableData(updatedData);
  };

  const handleDEliverableDelete: EditActionFn = (_row, index) => {
    const updatedData = deleteData(index, phaseWiseDeliverableData);
    setPhaseWiseDeliverableData(updatedData);
  };

  const putImportData = useMutation(
    (data: IPutProjectTemplateTDMImportPayload) =>
      putProjectTemplateTDMImport({
        templateId: Number(templateId),
        data,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([SherpaProjectReactQueryKey.GET_PROJECT_TEMPLATE]);
        navigate(`/app/sherpa/project-templates/${templateId}`);
      },
    }
  );

  const flattenPhaseWiseData = (phaseWiseData: any, tdmType: string) => {
    return Object.keys(phaseWiseData).reduce((acc: any[], cv: string) => {
      const fTask = phaseWiseData[cv as keyof typeof phaseWiseData];
      const transformedTask = fTask.map((t: any) => ({
        [tdmType]: t[tdmType],
        phase: t.phase.toUpperCase(),
        ...(t.category.value && { categoryId: t.category.value }),
        ...(t.lead.value && { leadTitleId: t.lead.value }),
        ...(t.support.value && { supportTitleId: t.support.value }),
      }));
      return [...acc, ...transformedTask];
    }, []);
  };

  const validateImportData = () => {
    let isValid = true;

    availablePhases.forEach((p: Phases) => {
      const phaseErrors = getPhaseErrors(p);
      const hasErrorsInPhase = phaseErrors.tasks.length > 0 || phaseErrors.deliverables.length > 0 || phaseErrors.milestones.length > 0;
      isValid = isValid && !hasErrorsInPhase;
    });

    return isValid;
  };

  const handleImport = () => {
    const isValidImport = validateImportData();

    if (isValidImport) {
      putImportData.mutate({
        resourceType: 'TASK',
        templateTdms: flattenPhaseWiseData(phaseWiseTaskData, 'task'),
      });

      putImportData.mutate({
        resourceType: 'DELIVERABLE',
        templateTdms: flattenPhaseWiseData(phaseWiseDeliverableData, 'deliverable'),
      });

      putImportData.mutate({
        resourceType: 'MILESTONE',
        templateTdms: flattenPhaseWiseData(phaseWiseMilestoneData, 'milestone'),
      });
    } else {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Import data is not valid. Please fix the errors and try again.',
          toastType: 'failed',
        })
      );
    }
  };

  return (
    <>
      <div className='flex justify-end'>
        <Button label='Import' btnStyle='primary' btnType='button' onClick={handleImport} />
      </div>
      <div className='flex justify-center items-center my-6'>
        <div className='flex flex-wrap justify-between p-5 bg-white rounded w-full shadow'>
          {availablePhases.map((phase, index) => {
            const phaseErrors = getPhaseErrors(phase);
            const hasErrorsInPhase =
              phaseErrors.tasks.length > 0 || phaseErrors.deliverables.length > 0 || phaseErrors.milestones.length > 0;
            return (
              <div
                key={phase}
                role='button'
                onClick={() => {
                  setCurrentPhase(index);
                }}
                className='flex gap-2'
              >
                <div
                  className={`rounded-full ${
                    hasErrorsInPhase ? 'bg-tertiaryColor-normal text-white' : 'bg-primaryColor-normal text-white'
                  } w-8 h-8 flex justify-center items-center`}
                >
                  {hasErrorsInPhase ? <XCircleIcon className='w-5 h-5' /> : <CheckIcon className='w-5 h-5' />}
                </div>
                <div
                  className={`${hasErrorsInPhase && currentPhase === index && 'text-tertiaryColor-normal'} ${
                    !hasErrorsInPhase && currentPhase === index && 'text-primaryColor-normal'
                  } cursor-pointer flex gap-2 items-center text-md`}
                >
                  {phase}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* {isLastAvailablePhase && availablePhases?.length > 0 && (
        <div className="flex items-center justify-center bg-white h-1/2">
          <div className="flex items-center justify-center flex-col gap-4">
            <div className="border-primaryColor-normal flex items-center justify-center text-primaryColor-normal border-2 rounded-full p-1">
              <CheckIcon className="w-6 h-6" />
            </div>
            <p>You are all set to import!</p>
            <div className="flex items-center justify-center gap-4">
              <Button
                label="Review Again"
                btnStyle="neutral"
                btnType="button"
                onClick={() => setCurrentPhase(0)}
              />
              <Button
                label="Import"
                btnStyle="primary"
                btnType="button"
                onClick={handleImport}
              />
            </div>
          </div>
        </div>
      )} */}

      {!isLastAvailablePhase && (
        <Accordion multi>
          {currentPhaseTaskData.length > 0 && (
            <div className='bg-white'>
              <AccordionItem
                id={`${currentPhaseName}-task`}
                title={
                  <div className='-ml-3.5 -my-3'>
                    <AccentTitle title='Tasks' />
                  </div>
                }
              >
                <Table
                  data={currentPhaseTaskData}
                  id='task'
                  headers={taskTableHeader}
                  actions={{ edit: true, delete: true }}
                  onDelete={handleTaskDelete}
                  onEdit={handleTaskEdit}
                  handleErrors={(row) => {
                    const categoryError = currentPhaseTaskErrors.categories[row.task];
                    const leadTitleError = currentPhaseTaskErrors.lead[row.task];
                    const supportTitleError = currentPhaseTaskErrors.support[row.task];

                    return {
                      view: (
                        <>
                          {categoryError && <div>{categoryError}</div>}
                          {leadTitleError && <div>{leadTitleError}</div>}
                          {supportTitleError && <div>{supportTitleError}</div>}
                        </>
                      ),
                      hasErrors: !!categoryError || !!leadTitleError || !!supportTitleError,
                    };
                  }}
                />
              </AccordionItem>
            </div>
          )}
          {currentPhaseDeliverableData.length > 0 && (
            <div className='bg-white'>
              <AccordionItem
                id={`${currentPhaseName}-deliverable`}
                title={
                  <div className='-ml-3.5 -my-3'>
                    <AccentTitle title='Deliverables' />
                  </div>
                }
              >
                <Table
                  id='deliverable'
                  data={currentPhaseDeliverableData}
                  headers={deliverableTableHeader}
                  actions={{ edit: true, delete: true }}
                  onDelete={handleDEliverableDelete}
                  onEdit={handleDeliverableEdit}
                  handleErrors={(row) => {
                    const leadTitleError = currentPhaseDeliverableErrors.lead[row.deliverable];
                    const supportTitleError = currentPhaseDeliverableErrors.support[row.deliverable];

                    return {
                      view: (
                        <>
                          {leadTitleError && <div>{leadTitleError}</div>}
                          {leadTitleError && <div>{supportTitleError}</div>}
                        </>
                      ),
                      hasErrors: !!leadTitleError || !!supportTitleError,
                    };
                  }}
                />
              </AccordionItem>
            </div>
          )}
          {currentPhaseMilestoneData.length > 0 && (
            <div className='bg-white'>
              <AccordionItem
                id={`${currentPhaseName}-milestone`}
                title={
                  <div className='-ml-3.5 -my-3'>
                    <AccentTitle title='Milestones' />
                  </div>
                }
              >
                <Table
                  id='milestone'
                  data={currentPhaseMilestoneData}
                  headers={milestoneTableHeader}
                  actions={{ edit: true, delete: true }}
                  onDelete={handleMilestoneDelete}
                  onEdit={handleMilestoneEdit}
                  handleErrors={(row) => {
                    const leadTitleError = currentPhaseMilestoneErrors.lead[row.milestone];
                    const supportTitleError = currentPhaseMilestoneErrors.support[row.milestone];

                    return {
                      view: (
                        <>
                          {leadTitleError && <div>{leadTitleError}</div>}
                          {leadTitleError && <div>{supportTitleError}</div>}
                        </>
                      ),
                      hasErrors: !!leadTitleError || !!supportTitleError,
                    };
                  }}
                />
              </AccordionItem>
            </div>
          )}
        </Accordion>
      )}
      {/* {availablePhases.length > 0 && (
        <div className="flex items-center justify-end p-4 gap-4">
          {currentPhase > 0 && !isLastAvailablePhase && (
            <Button
              label="Previous"
              btnStyle="neutral"
              btnType="button"
              onClick={() => setCurrentPhase((prev) => prev - 1)}
            />
          )}

          {currentPhase < availablePhases.length && (
            <Button
              label="Next"
              btnStyle={`${hasErrors ? "neutral" : "primary"}`}
              customClassName={
                hasErrors
                  ? "cursor-not-allowed border border-neutralColor-light"
                  : ""
              }
              disabled={hasErrors}
              btnType="button"
              onClick={() => setCurrentPhase((prev) => prev + 1)}
            />
          )}
        </div>
      )} */}
    </>
  );
};
